import { EnumUserTypes, HiringCompanyType } from "@utils/constants";
import { IAvailabilitySlot } from "../MeetingDetailPage";
import store from "@store/index";
import { IGeneralSlotResponse } from "@endpoints/types/interview.type";

const getCurrentUser = () => store.getState()?.auth?.user;

const getHiringTeamMemberAvailabilityRecord = (slot: IGeneralSlotResponse) => {
    const currentUser = getCurrentUser();
    const currentUserId = currentUser?._id || currentUser?.userId;

    if (!currentUserId || !Array.isArray(slot?.hiringTeamAvailability)) {
        return undefined;
    }

    const memberAvailability = slot.hiringTeamAvailability.find((item) => {
        const memberId =
            typeof item?.memberId === "string"
                ? item.memberId
                : item?.memberId?._id;
        return String(memberId) === String(currentUserId);
    });

    return memberAvailability;
};

const getHiringTeamMemberAvailability = (slot: IGeneralSlotResponse) => {
    return getHiringTeamMemberAvailabilityRecord(slot)?.isAvailable;
};

const isCurrentUserAssignedHiringMember = (slot: IGeneralSlotResponse) => {
    const currentUser = getCurrentUser();
    const currentUserId = currentUser?._id || currentUser?.userId;

    if (currentUser?.userType !== EnumUserTypes.HIRING_COMPANY) {
        return false;
    }

    if (!currentUserId || !Array.isArray(slot?.hiringTeamAvailability)) {
        return false;
    }

    return !!getHiringTeamMemberAvailabilityRecord(slot);
};

// To check if the slot is disabled for current user due to slot declined by another user (Candidate | Company | Recruiter)
export function checkIsSlotDisabled(slot: IAvailabilitySlot) {
    const currentUserType = getCurrentUser()?.userType
    if (currentUserType === EnumUserTypes.CANDIDATE) {
        return slot?.isCompanyAvailable === false || slot?.isRecruiterAvailable === false
    } else if (currentUserType === EnumUserTypes.RECRUITER) {
        return slot?.isCompanyAvailable === false || slot?.isCandidateAvailable === false
    }
    else if (currentUserType === EnumUserTypes.HIRING_COMPANY) {
        return slot?.isCandidateAvailable === false || slot?.isRecruiterAvailable === false
    }
}


// Check if the current user has responded all the given slots or not
export function isAllSlotResponded(
    slots: IAvailabilitySlot[],
    isAssignedHiringTeamMember?: boolean
) {
    const currentUserType = getCurrentUser()?.userType
    if (currentUserType === EnumUserTypes.CANDIDATE) {
        return slots?.every((slot) => {
            return slot?.isCandidateAvailable || slot?.isCandidateAvailable === false || checkIsSlotDisabled(slot)
        })
    } else if (currentUserType === EnumUserTypes.RECRUITER) {
        return slots?.every((slot) => {
            return slot?.isRecruiterAvailable || slot?.isRecruiterAvailable === false || checkIsSlotDisabled(slot)
        })
    }
    else if (currentUserType === EnumUserTypes.HIRING_COMPANY) {
        const isHiringTeamMember =
            typeof isAssignedHiringTeamMember === "boolean"
                ? isAssignedHiringTeamMember
                : slots?.length
                ? isCurrentUserAssignedHiringMember(slots[0])
                : false;

        if (slots?.length && isHiringTeamMember) {
            return slots?.every((slot) => {
                const memberAvailability = getHiringTeamMemberAvailability(slot);
                return (
                    memberAvailability === true ||
                    memberAvailability === false ||
                    checkIsSlotDisabled(slot)
                );
            });
        }
        return slots?.every((slot) => {
            return slot?.isCompanyAvailable || slot?.isCompanyAvailable === false || checkIsSlotDisabled(slot)
        })
    }
}

// Check if the current slot qualifies for final invite confirmation.
// A slot is eligible when all participants except the current user have accepted it.
export function isSlotReadyForInvite(slots: IAvailabilitySlot[]) {
    const currentUserType = getCurrentUser()?.userType
    if (currentUserType === EnumUserTypes.CANDIDATE) {
        return slots?.some((slot) => slot?.isCompanyAvailable && slot?.isRecruiterAvailable)
    } else if (currentUserType === EnumUserTypes.RECRUITER) {
        return slots?.some((slot) => slot?.isCompanyAvailable && slot?.isCandidateAvailable)
    }
    else if (currentUserType === EnumUserTypes.HIRING_COMPANY) {
        return slots?.some((slot) => slot?.isCandidateAvailable && slot?.isRecruiterAvailable)
    }
}


export type slotStatus = "Accepted" | "Declined" | "Pending" | 'Auto-Declined';

export function getHiringTeamResponseSummary(slot: IGeneralSlotResponse) {
    const hiringTeamAvailability = slot?.hiringTeamAvailability || [];

    if (!hiringTeamAvailability.length) {
        return "Pending";
    }

    if (hiringTeamAvailability.some((item) => item?.isAvailable === undefined)) {
        return "Pending";
    }

    if (hiringTeamAvailability.every((item) => item?.isAvailable === true)) {
        return "Accepted";
    }

    if (hiringTeamAvailability.every((item) => item?.isAvailable === false)) {
        return "Declined";
    }

    return "Pending";
}

// Return the slot status label based on particular user
// export function getSlotStatus({
//     slot,
//     statusOf,
// }: {
//     slot: IGeneralSlotResponse;
//     statusOf?: EnumUserTypes;
// }): slotStatus {
//     const isProposedBy = slot?.proposedBy?.userType;

//     if (isProposedBy === EnumUserTypes.RECRUITER) {
//         if (
//             (!slot?.isCompanyAvailable &&
//                 slot?.isCompanyAvailable === false &&
//                 statusOf === EnumUserTypes.HIRING_COMPANY) ||
//             (!slot?.isCandidateAvailable &&
//                 slot?.isCandidateAvailable === false &&
//                 statusOf === EnumUserTypes.CANDIDATE)
//         ) {
//             return "Declined";
//         } else if (
//             (slot?.isCompanyAvailable &&
//                 slot?.isCompanyAvailable === true &&
//                 statusOf === EnumUserTypes.HIRING_COMPANY) ||
//             (slot?.isCandidateAvailable &&
//                 slot?.isCandidateAvailable === true &&
//                 statusOf === EnumUserTypes.CANDIDATE)
//         ) {
//             return "Accepted";
//         } else if (
//             (slot?.isCandidateAvailable === false &&
//                 statusOf === EnumUserTypes.HIRING_COMPANY) ||
//             (slot?.isCompanyAvailable === false &&
//                 statusOf === EnumUserTypes.CANDIDATE)
//         ) {
//             return 'Auto-Declined';
//         } else {
//             return "Pending";
//         }
//     }

//     if (isProposedBy === EnumUserTypes.CANDIDATE) {
//         if (
//             (!slot?.isCompanyAvailable &&
//                 slot?.isCompanyAvailable === false &&
//                 statusOf === EnumUserTypes.HIRING_COMPANY) ||
//             (!slot?.isRecruiterAvailable &&
//                 slot?.isRecruiterAvailable === false &&
//                 statusOf === EnumUserTypes.RECRUITER)
//         ) {
//             return "Declined";
//         } else if (
//             (slot?.isCompanyAvailable &&
//                 slot?.isCompanyAvailable === true &&
//                 statusOf === EnumUserTypes.HIRING_COMPANY) ||
//             (slot?.isRecruiterAvailable &&
//                 slot?.isRecruiterAvailable === true &&
//                 statusOf === EnumUserTypes.RECRUITER)
//         ) {
//             return "Accepted";
//         } else if (
//             (slot?.isRecruiterAvailable === false &&
//                 statusOf === EnumUserTypes.HIRING_COMPANY) ||
//             (slot?.isCompanyAvailable === false &&
//                 statusOf === EnumUserTypes.RECRUITER)
//         ) {
//             return 'Auto-Declined';
//         }
//         else {
//             return "Pending";
//         }
//     }

//     if (isProposedBy === EnumUserTypes.HIRING_COMPANY) {
//         if (
//             (!slot?.isCandidateAvailable &&
//                 slot?.isCandidateAvailable === false &&
//                 statusOf === EnumUserTypes.CANDIDATE) ||
//             (!slot?.isRecruiterAvailable &&
//                 slot?.isRecruiterAvailable === false &&
//                 statusOf === EnumUserTypes.RECRUITER)
//         ) {
//             return "Declined";
//         } else if (
//             (slot?.isCandidateAvailable &&
//                 slot?.isCandidateAvailable === true &&
//                 statusOf === EnumUserTypes.CANDIDATE) ||
//             (slot?.isRecruiterAvailable &&
//                 slot?.isRecruiterAvailable === true &&
//                 statusOf === EnumUserTypes.RECRUITER)
//         ) {
//             return "Accepted";
//         } else if (
//             (slot?.isRecruiterAvailable === false &&
//                 statusOf === EnumUserTypes.CANDIDATE) ||
//             (slot?.isCandidateAvailable === false &&
//                 statusOf === EnumUserTypes.RECRUITER)
//         ) {
//             return 'Auto-Declined';
//         } else {
//             return "Pending";
//         }
//     }
// }
export function getSlotStatus({
    slot,
    statusOf,
    isAssignedHiringTeamMember,
}: {
    slot: IGeneralSlotResponse;
    statusOf?: EnumUserTypes;
    isAssignedHiringTeamMember?: boolean;
}): slotStatus {
    const currentUser = getCurrentUser();
    const isHiringTeamMember =
        typeof isAssignedHiringTeamMember === "boolean"
            ? isAssignedHiringTeamMember
            : isCurrentUserAssignedHiringMember(slot);
    const hiringTeamMemberAvailability = getHiringTeamMemberAvailability(slot);
    const targetStatusOf = statusOf ?? currentUser?.userType;
    const isProposedBy = slot?.proposedBy?.userType;

    const getHiringTeamMemberStatus = () => {
        if (!isHiringTeamMember) return undefined;

        if (hiringTeamMemberAvailability === true) return "Accepted";
        if (hiringTeamMemberAvailability === false) return "Declined";
        return "Pending";
    };

    if (isProposedBy === EnumUserTypes.RECRUITER) {
        if (targetStatusOf === EnumUserTypes.HIRING_COMPANY && isHiringTeamMember) {
            const hiringTeamMemberStatus = getHiringTeamMemberStatus();
            if (hiringTeamMemberStatus) return hiringTeamMemberStatus;
        }

        if (
            (!slot?.isCompanyAvailable &&
                slot?.isCompanyAvailable === false &&
                targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
            (!slot?.isCandidateAvailable &&
                slot?.isCandidateAvailable === false &&
                targetStatusOf === EnumUserTypes.CANDIDATE)
        ) {
            return "Declined";
        } else if (
            (slot?.isCompanyAvailable &&
                slot?.isCompanyAvailable === true &&
                targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
            (slot?.isCandidateAvailable &&
                slot?.isCandidateAvailable === true &&
                targetStatusOf === EnumUserTypes.CANDIDATE)
        ) {
            return "Accepted";
        } else if (
            ((slot?.isRecruiterAvailable === false ||
                slot?.isCandidateAvailable === false) &&
                slot?.isCompanyAvailable !== false &&
                targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
            ((slot?.isRecruiterAvailable === false ||
                slot?.isCompanyAvailable === false) &&
                slot?.isCandidateAvailable !== false &&
                targetStatusOf === EnumUserTypes.CANDIDATE)
        ) {
            return "Auto-Declined";
        } else {
            return "Pending";
        }
    }

    if (isProposedBy === EnumUserTypes.CANDIDATE) {
        if (targetStatusOf === EnumUserTypes.HIRING_COMPANY && isHiringTeamMember) {
            const hiringTeamMemberStatus = getHiringTeamMemberStatus();
            if (hiringTeamMemberStatus) return hiringTeamMemberStatus;
        }

        if (
            (!slot?.isCompanyAvailable &&
                slot?.isCompanyAvailable === false &&
                targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
            (!slot?.isRecruiterAvailable &&
                slot?.isRecruiterAvailable === false &&
                targetStatusOf === EnumUserTypes.RECRUITER)
        ) {
            return "Declined";
        } else if (
            (slot?.isCompanyAvailable &&
                slot?.isCompanyAvailable === true &&
                targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
            (slot?.isRecruiterAvailable &&
                slot?.isRecruiterAvailable === true &&
                targetStatusOf === EnumUserTypes.RECRUITER)
        ) {
            return "Accepted";
        } else if (
            ((slot?.isRecruiterAvailable === false ||
                slot?.isCandidateAvailable === false) &&
                slot?.isCompanyAvailable !== false &&
                targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
            ((slot?.isCandidateAvailable === false ||
                slot?.isCompanyAvailable === false) &&
                slot?.isRecruiterAvailable !== false &&
                targetStatusOf === EnumUserTypes.RECRUITER)
        ) {
            return "Auto-Declined";
        } else {
            return "Pending";
        }
    }

    if (isProposedBy === EnumUserTypes.HIRING_COMPANY) {
        if (targetStatusOf === EnumUserTypes.HIRING_COMPANY) {
            if (isHiringTeamMember) {
                const hiringTeamMemberStatus = getHiringTeamMemberStatus();
                if (hiringTeamMemberStatus) return hiringTeamMemberStatus;
            }
            if (slot?.isCompanyAvailable === true) return "Accepted";
            if (slot?.isCompanyAvailable === false) return "Declined";
        }

        if (
            (!slot?.isCandidateAvailable &&
                slot?.isCandidateAvailable === false &&
                targetStatusOf === EnumUserTypes.CANDIDATE) ||
            (!slot?.isRecruiterAvailable &&
                slot?.isRecruiterAvailable === false &&
                targetStatusOf === EnumUserTypes.RECRUITER)
        ) {
            return "Declined";
        } else if (
            (slot?.isCandidateAvailable &&
                slot?.isCandidateAvailable === true &&
                targetStatusOf === EnumUserTypes.CANDIDATE) ||
            (slot?.isRecruiterAvailable &&
                slot?.isRecruiterAvailable === true &&
                targetStatusOf === EnumUserTypes.RECRUITER)
        ) {
            return "Accepted";
        } else if (
            ((slot?.isRecruiterAvailable === false ||
                slot?.isCompanyAvailable === false) &&
                slot?.isRecruiterAvailable !== false &&
                targetStatusOf === EnumUserTypes.RECRUITER) ||
            ((slot?.isRecruiterAvailable === false ||
                slot?.isCompanyAvailable === false) &&
                slot?.isCandidateAvailable !== false &&
                targetStatusOf === EnumUserTypes.CANDIDATE)
        ) {
            return "Auto-Declined";
        } else {
            return "Pending";
        }
    }
}
